import React from "react";
import moment from "moment";
import * as Mui from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import * as Icons from "react-feather";
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from "react-redux";
import { bindActionCreators, Dispatch } from "redux";
import { RootState, getPayload } from "redux/store";
import { getLeadProfileSuggestionsForLead } from "redux/selector";
import {
	acceptLeadProfileSuggestions,
	dismissLeadProfileSuggestions,
} from "redux/slice/search-agent";
import { Table, Column } from "component/shared/table";
import { FeatherIcon } from "component/shared/feather-icon";
import { LeadProfileSuggestion } from "model/lead-profile-suggestion";
import { LeadProfileSuggestionReferenceResource } from "api/resource/lead-profile-suggestion";
import { LeadProfileSuggestionField } from "type/lead-profile-suggestion-field";
import { SearchAgentConversationDay } from "model/search-agent-profile";
import { UserPenIcon } from "../icons";
import { ConversationTranscriptDialog } from "../conversation-transcript-dialog";
import { formatLeadProfileSuggestionValue } from "./format";
import { styles } from "./style";

interface OwnProps {
	leadId: number;
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	return {
		suggestions: getLeadProfileSuggestionsForLead(state, ownProps.leadId),
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => bindActionCreators({
	acceptLeadProfileSuggestions,
	dismissLeadProfileSuggestions,
}, dispatch);

interface Props extends
	OwnProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {
}

type Action = "accept" | "dismiss";

interface State {
	expanded: boolean;
	/** Selected rows by field; a lead has at most one suggestion per field. */
	selectedFields: LeadProfileSuggestionField[];
	busy: boolean;
	failedAction?: Action;
	/** The suggestion whose conversation is open, or undefined when none is. */
	transcriptSuggestion?: LeadProfileSuggestion;
}

/**
 * The transcript dialog only needs the day's date to fetch and title it, so the day is built
 * from the suggestion rather than looked up in the conversation history — which may not have
 * loaded yet.
 */
const toReference = (suggestion: LeadProfileSuggestion): LeadProfileSuggestionReferenceResource => ({
	field: suggestion.field.id,
	suggestedOn: suggestion.suggestedOnReference,
});

const toConversationDay = (conversationDate: string): SearchAgentConversationDay => ({
	date: moment(conversationDate, "YYYY-MM-DD"),
	messageCount: 0,
	aiSessionIds: [],
});

class Component extends React.Component<Props, State> {

	public readonly state: State = {
		expanded: false,
		selectedFields: [],
		busy: false,
	};

	/**
	 * Selection outlives the rows it named — a refetch after an edit can withdraw one — so it is
	 * read through the rows currently shown.
	 */
	private get selected() {
		const { suggestions } = this.props;
		const { selectedFields } = this.state;
		return suggestions.filter((suggestion) => selectedFields.includes(suggestion.field));
	}

	private get columns(): Column<LeadProfileSuggestion>[] {
		const { classes = {} } = this.props;
		return [
			{
				title: "Field",
				value: (suggestion) => (
					<span className={classes.field}>{suggestion.field.label}</span>
				),
			},
			{
				title: "Current",
				value: (suggestion) => (
					<span className={classes.current}>
						{formatLeadProfileSuggestionValue(suggestion.field, suggestion.current)}
					</span>
				),
			},
			{
				title: "Suggested Update",
				value: (suggestion) => (
					<span className={classes.suggested}>
						{formatLeadProfileSuggestionValue(suggestion.field, suggestion.suggested)}
					</span>
				),
			},
			{
				title: "Conversation",
				value: (suggestion) => (
					<div className={classes.conversation}>
						{suggestion.conversationDate && (
							<Mui.Link
								component="button"
								type="button"
								className={classes.viewConversation}
								onClick={() => this.setState({ transcriptSuggestion: suggestion })}
							>
								View conversation
							</Mui.Link>
						)}
					</div>
				),
			},
			{
				title: "Date",
				value: (suggestion) => (
					<span className={classes.date}>{suggestion.suggestedOn.format("MMM D")}</span>
				),
			},
		];
	}

	public render() {
		const { suggestions, leadId, classes = {} } = this.props;
		const { expanded, busy, failedAction, transcriptSuggestion } = this.state;
		if (!suggestions.length) {
			return null;
		}
		const selected = this.selected;
		const count = suggestions.length;
		return (
			<>
				<Mui.Accordion
					expanded={expanded}
					onChange={(event, isExpanded) => this.setState({ expanded: isExpanded })}
					disableGutters
					elevation={0}
					className={classes.card}
				>
					<Mui.AccordionSummary
						expandIcon={<FeatherIcon className={classes.chevron}><Icons.ChevronDown /></FeatherIcon>}
						className={classes.summary}
						classes={{ content: classes.summaryContent }}
					>
						<UserPenIcon className={classes.icon} />
						<Mui.Typography className={classes.title}>
							{count} suggested profile {count === 1 ? "update" : "updates"}
						</Mui.Typography>
					</Mui.AccordionSummary>
					<Mui.AccordionDetails className={classes.details}>
						<div className={classes.table}>
							<Table
								items={suggestions}
								columns={this.columns}
								hidePagination
								pageSize={count}
								isSelected={(suggestion) => selected.some((item) => item.field === suggestion.field)}
								onSelect={this.handleSelect}
								onUnselect={this.handleUnselect}
							/>
						</div>
						<div className={classes.footer}>
							<Mui.Typography className={classes.selectedCount}>
								{selected.length} selected
							</Mui.Typography>
							<Mui.Button
								variant="outlined"
								color="primary"
								disabled={busy || !selected.length}
								onClick={() => this.act("dismiss", selected)}
							>
								Dismiss
							</Mui.Button>
							<Mui.Button
								variant="contained"
								color="primary"
								disabled={busy || !selected.length}
								onClick={() => this.act("accept", selected)}
							>
								Accept
							</Mui.Button>
						</div>
					</Mui.AccordionDetails>
				</Mui.Accordion>
				{transcriptSuggestion && transcriptSuggestion.conversationDate && (
					<ConversationTranscriptDialog
						key={transcriptSuggestion.field.id}
						open
						onClose={this.handleCloseTranscript}
						leadId={leadId}
						conversationDay={toConversationDay(transcriptSuggestion.conversationDate)}
						suggestion={transcriptSuggestion}
						onAcceptSuggestion={(suggestion) => this.act("accept", [suggestion])}
						onDismissSuggestion={(suggestion) => this.act("dismiss", [suggestion])}
					/>
				)}
				<Mui.Snackbar
					open={!!failedAction}
					autoHideDuration={4000}
					onClose={() => this.setState({ failedAction: undefined })}
				>
					<MuiAlert severity="error">
						<Mui.Typography>
							{failedAction === "accept"
								? "Failed to accept suggested profile updates"
								: "Failed to dismiss suggested profile updates"}
						</Mui.Typography>
					</MuiAlert>
				</Mui.Snackbar>
			</>
		);
	}

	private handleSelect = (suggestions: LeadProfileSuggestion[]) => {
		const fields = suggestions.map((suggestion) => suggestion.field);
		this.setState((state) => ({
			selectedFields: [...state.selectedFields.filter((field) => !fields.includes(field)), ...fields],
		}));
	};

	private handleUnselect = (suggestions: LeadProfileSuggestion[]) => {
		const fields = suggestions.map((suggestion) => suggestion.field);
		this.setState((state) => ({
			selectedFields: state.selectedFields.filter((field) => !fields.includes(field)),
		}));
	};

	private handleCloseTranscript = () => {
		this.setState({ transcriptSuggestion: undefined });
	};

	/**
	 * Shared by the table and the transcript banner, so both report a failure the same way.
	 * Resolves whether it succeeded. A failure leaves the rows, and their selection, in place.
	 */
	private act = async (action: Action, suggestions: LeadProfileSuggestion[]): Promise<boolean> => {
		const { leadId } = this.props;
		this.setState({ busy: true, failedAction: undefined });
		const thunk = action === "accept"
			? this.props.acceptLeadProfileSuggestions
			: this.props.dismissLeadProfileSuggestions;
		// A dispatched thunk resolves with its settled action rather than throwing. Only a
		// fulfilled action carries a payload.
		const succeeded = !!getPayload(await thunk({ leadId, suggestions: suggestions.map(toReference) }));
		const fields = suggestions.map((suggestion) => suggestion.field);
		this.setState((state) => ({
			busy: false,
			failedAction: succeeded ? undefined : action,
			selectedFields: succeeded
				? state.selectedFields.filter((field) => !fields.includes(field))
				: state.selectedFields,
		}));
		return succeeded;
	};
}

export const SuggestedProfileUpdatesCard = withStyles(
	connect(mapStateToProps, mapDispatchToProps)(Component),
	styles
);
